import { useEffect, useRef, useState, type SyntheticEvent } from 'react';

import { feel } from './native-feel/index.js';

/**
 * Presentation-only AI assistant widget. Extracted from dashboard's
 * AskKilat per [[feedback-unified-packages-ui]] — the customer-pwa is
 * about to mount the same shape (Ask the carwash, white-label-themed)
 * so the component lifts into `@kilat/ui` BEFORE the second copy lands.
 *
 * Decoupled from the callable: caller passes `onAsk(question)` and gets
 * back the parsed response. That way the dashboard wires it to the
 * Kilat-branded `askKilat` callable while customer-pwa wires it to the
 * tenant-scoped `askCarwash` callable — same UI, different backend.
 *
 * Brand-safe via CSS variables. Defaults to Kilat green when vars
 * aren't loaded so the widget still looks intentional on the splash.
 * Custom-crafted per [[feedback-premium-custom-ui]]: chip-card pattern,
 * not generic chatbot speech bubbles; bespoke sparkle SVG; soft motion.
 *
 * Per [[feedback-no-dead-ends]]:
 *   - typing-dots while pending
 *   - STUB tag + amber explainer on NoOp answers
 *   - CACHED tag on cache-hit answers
 *   - rate-limit countdown via the structured error shape
 *
 * Per [[feedback-alive-haptic-sound-feedback]]: `feel('tap')` on
 * open + send, `feel('success')` on answer.
 */

export interface AskAssistantResponse {
  readonly text: string;
  readonly cacheHit: boolean;
  /** 'noop' → render STUB explainer; anything else → real answer. */
  readonly provider: string;
}

export interface AskAssistantError {
  readonly message: string;
  /** When the server returned `resource-exhausted`, the seconds-until-retry hint. */
  readonly retryAfterSec?: number;
}

export interface AskAssistantProps {
  /** Caller's typed wrapper around their callable. Throws on failure. */
  readonly onAsk: (question: string) => Promise<AskAssistantResponse>;
  /** Parser for the caller's error shape (likely the shared parseRateLimitError). */
  readonly parseError: (err: unknown) => AskAssistantError;
  /** Persona-flavoured copy. */
  readonly launcherLabel: string; // e.g., "Ask Kilat"
  readonly headerTitle: string; // e.g., "Ask Kilat"
  readonly headerSubtitle: string; // e.g., "Your dashboard assistant — features, drafts, ideas."
  readonly placeholder: string; // textarea placeholder
  readonly emptyStateBody: string;
  readonly suggestionChips: readonly string[];
  /** Copy shown on the amber explainer when answer comes back with provider='noop'. */
  readonly noopExplainer: string;
  /** Storage key for the localStorage exchange history; keep distinct per app. */
  readonly storageKey: string;
  /** Up to how many recent exchanges to persist. */
  readonly historyLimit?: number;
  /** Floating launcher offset from bottom — distinct per app's safe-area habits. */
  readonly bottomOffset?: string;
  /**
   * Where the launcher lives.
   *
   * `'fixed'` (default) pins a wide pill to the bottom-right corner. That is
   * what every app shipped with, and on the dashboard it was MEASURABLY sitting
   * on top of page content — the /queue "Manage bays" button was underneath it
   * and could not be clicked. The house rule is to slot controls into existing
   * headers rather than invent corners, so `'inline'` renders the launcher as a
   * normal in-flow button the caller places itself (the dashboard puts it in
   * the app header next to the notification bell). The panel it opens is fixed
   * either way.
   *
   * Measured at 390px, the labelled fixed pill covered **51% of "Save and
   * continue"** on the customer PWA's first-run profile form and 48% of a
   * service card on /book, and a tap in the overlap opened the assistant
   * instead of doing what the customer meant. Shrinking it helped; only moving
   * it out of the content area fixed it. Both apps are now `'inline'`.
   */
  readonly launcherMode?: 'fixed' | 'inline';
  /**
   * Drop the label, leaving the sparkle alone.
   *
   * Separate from `launcherMode` because placement and size are independent
   * questions. The labelled pill is 178px wide, which is fine in a desktop
   * header and far too much in a phone's chrome row next to a language pill and
   * a notification bell.
   *
   * The label survives as the accessible name and the tooltip, so this costs a
   * screen reader nothing.
   */
  readonly launcherCompact?: boolean;
  /**
   * Chrome copy. These four were hardcoded English inside a component that
   * ships in the CUSTOMER-facing PWA, so a Malay or Tamil speaker saw "You" and
   * "Clear recent" regardless of their choice. Defaulted to English so no
   * caller breaks, but now overridable — the lint rule was right to flag them.
   */
  readonly youLabel?: string;
  readonly clearHistoryLabel?: string;
  /** Rate-limit note; receives the seconds remaining. */
  readonly retryAfterLabel?: (seconds: number) => string;
}

interface Exchange {
  readonly id: string;
  readonly question: string;
  readonly answer: string;
  readonly cacheHit: boolean;
  readonly provider: string;
  readonly askedAtMs: number;
}

function loadHistory(key: string, limit: number): Exchange[] {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.slice(0, limit) as Exchange[];
  } catch {
    return [];
  }
}

function saveHistory(key: string, history: readonly Exchange[], limit: number): void {
  try {
    window.localStorage.setItem(key, JSON.stringify(history.slice(0, limit)));
  } catch {
    /* swallow */
  }
}

export function AskAssistant(props: AskAssistantProps) {
  const {
    onAsk,
    parseError,
    launcherLabel,
    launcherMode = 'fixed',
    youLabel = 'You',
    clearHistoryLabel = 'Clear recent',
    retryAfterLabel = (seconds: number) => `Available again in ${seconds}s.`,
    headerTitle,
    headerSubtitle,
    placeholder,
    emptyStateBody,
    suggestionChips,
    noopExplainer,
    storageKey,
    historyLimit = 5,
    bottomOffset = 'calc(var(--safe-bottom, 0px) + 24px)',
    launcherCompact = false,
  } = props;

  const [open, setOpen] = useState(false);
  const [question, setQuestion] = useState('');
  const [history, setHistory] = useState<Exchange[]>(() => loadHistory(storageKey, historyLimit));
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retryAfterSec, setRetryAfterSec] = useState<number | null>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (retryAfterSec === null) return;
    if (retryAfterSec <= 0) {
      setRetryAfterSec(null);
      setError(null);
      return;
    }
    const id = window.setTimeout(() => setRetryAfterSec((s) => (s === null ? null : s - 1)), 1000);
    return () => window.clearTimeout(id);
  }, [retryAfterSec]);

  useEffect(() => {
    if (open) window.setTimeout(() => inputRef.current?.focus(), 120);
  }, [open]);

  function openPanel() {
    feel('tap');
    setOpen(true);
  }
  function closePanel() {
    setOpen(false);
  }

  async function submit(e: SyntheticEvent) {
    e.preventDefault();
    const q = question.trim();
    if (!q || pending || retryAfterSec !== null) return;
    feel('tap');
    setPending(true);
    setError(null);
    setQuestion('');
    try {
      const result = await onAsk(q);
      const next: Exchange = {
        id: `${Date.now()}`,
        question: q,
        answer: result.text,
        cacheHit: result.cacheHit,
        provider: result.provider,
        askedAtMs: Date.now(),
      };
      const merged = [next, ...history].slice(0, historyLimit);
      setHistory(merged);
      saveHistory(storageKey, merged, historyLimit);
      feel('success');
    } catch (err) {
      const parsed = parseError(err);
      setError(parsed.message);
      if (typeof parsed.retryAfterSec === 'number' && parsed.retryAfterSec > 0) {
        setRetryAfterSec(parsed.retryAfterSec);
      }
      // Restore the question so the user doesn't lose their thinking.
      setQuestion(q);
    } finally {
      setPending(false);
    }
  }

  function clearHistory() {
    setHistory([]);
    saveHistory(storageKey, [], historyLimit);
  }

  function applySuggestion(text: string) {
    setQuestion(text);
    window.setTimeout(() => inputRef.current?.focus(), 0);
  }

  return (
    <>
      {/* Always mounted, even while the panel is open: unmounting it made the
          header it sits in reflow (the cluster lost an item) every time the
          assistant opened. A fixed launcher still hides — it would sit over
          the panel. */}
      {!open || launcherMode === 'inline' ? (
        <button
          type="button"
          onClick={openPanel}
          aria-label={launcherLabel}
          aria-expanded={open}
          aria-haspopup="dialog"
          title={launcherCompact ? launcherLabel : undefined}
          style={{
            ...(launcherMode === 'fixed'
              ? { position: 'fixed', bottom: bottomOffset, right: 24, zIndex: 40 }
              : { position: 'relative' }),
            display: 'inline-flex',
            alignItems: 'center',
            gap: launcherMode === 'inline' ? 6 : 10,
            padding: launcherMode === 'inline' ? '0 14px' : '12px 18px 12px 14px',
            minHeight: launcherMode === 'inline' ? 'var(--touch-target, 44px)' : undefined,
            whiteSpace: 'nowrap',
            borderRadius: 999,
            border: 'none',
            background:
              'linear-gradient(135deg, var(--color-primary, #475569) 0%, var(--color-primary-light, #334155) 100%)',
            color: 'var(--color-primary-fg, #ffffff)',
            fontWeight: 600,
            fontSize: 13,
            cursor: 'pointer',
            boxShadow:
              launcherMode === 'inline'
                ? '0 1px 2px rgba(var(--color-primary-rgb), 0.24)'
                : '0 1px 2px rgba(var(--color-primary-rgb), 0.32),' +
                  '0 12px 28px rgba(var(--color-primary-rgb), 0.30),' +
                  '0 24px 48px rgba(var(--color-primary-rgb), 0.18)',
            fontFamily: 'inherit',
            transition: 'transform 160ms ease',
            // Last, so nothing above can undo it (padding used to).
            ...(launcherCompact
              ? {
                  // A circle, not a pill: width must not grow with the label,
                  // because the label length is per tenant and per locale and
                  // the whole point of this mode is a predictable footprint.
                  width: 'var(--touch-target, 2.75rem)',
                  height: 'var(--touch-target, 2.75rem)',
                  padding: 0,
                  borderRadius: '999px',
                  justifyContent: 'center',
                }
              : null),
          }}
          onMouseDown={(e) =>
            ((e.currentTarget as HTMLButtonElement).style.transform = 'scale(0.97)')
          }
          onMouseUp={(e) => ((e.currentTarget as HTMLButtonElement).style.transform = 'scale(1)')}
          onMouseLeave={(e) =>
            ((e.currentTarget as HTMLButtonElement).style.transform = 'scale(1)')
          }
        >
          <SparkleIcon />
          {launcherCompact ? null : launcherLabel}
        </button>
      ) : null}

      {open ? (
        <div
          role="dialog"
          aria-label={headerTitle}
          style={{
            position: 'fixed',
            bottom: bottomOffset,
            right: 24,
            width: 'min(420px, calc(100vw - 32px))',
            maxHeight: 'min(640px, calc(100vh - 64px))',
            zIndex: 50,
            display: 'flex',
            flexDirection: 'column',
            background: 'var(--color-surface, #ffffff)',
            color: 'var(--color-on-surface, #0f172a)',
            borderRadius: 20,
            border: '1px solid var(--color-border)',
            boxShadow:
              '0 1px 2px var(--tint-subtle),' +
              '0 24px 48px var(--color-border-strong),' +
              '0 48px 96px rgba(71,85,105,0.10)',
            overflow: 'hidden',
            animation: 'kilatAskRise 220ms ease-out',
          }}
        >
          <style>{`
            @keyframes kilatAskRise {
              from { transform: translateY(20px); opacity: 0; }
              to { transform: translateY(0); opacity: 1; }
            }
            @keyframes kilatAskDot {
              0%, 80%, 100% { opacity: 0.2; transform: scale(0.8); }
              40% { opacity: 1; transform: scale(1); }
            }
          `}</style>

          <header
            style={{
              padding: '14px 16px',
              borderBottom: '1px solid var(--color-border)',
              display: 'flex',
              alignItems: 'center',
              gap: 10,
            }}
          >
            <span
              aria-hidden
              style={{
                display: 'inline-grid',
                placeItems: 'center',
                width: 32,
                height: 32,
                borderRadius: 10,
                background:
                  'linear-gradient(135deg, var(--color-primary, #475569) 0%, var(--color-primary-light, #334155) 100%)',
                color: 'var(--color-primary-fg, #ffffff)',
              }}
            >
              <SparkleIcon />
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ margin: 0, fontWeight: 600, fontSize: 14 }}>{headerTitle}</p>
              <p
                style={{
                  margin: '2px 0 0 0',
                  fontSize: 11,
                  color: 'var(--color-on-surface-muted, #64748b)',
                }}
              >
                {headerSubtitle}
              </p>
            </div>
            <button
              type="button"
              onClick={closePanel}
              aria-label="Close"
              style={{
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                padding: 6,
                color: 'var(--color-on-surface-muted, #64748b)',
                fontSize: 18,
                lineHeight: 1,
              }}
            >
              ×
            </button>
          </header>

          <div
            style={{
              flex: 1,
              overflowY: 'auto',
              padding: '14px 16px',
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
            }}
          >
            {history.length === 0 && !pending ? (
              <EmptyState
                body={emptyStateBody}
                suggestions={suggestionChips}
                onApply={applySuggestion}
              />
            ) : null}

            {pending ? (
              <div style={exchangeCardStyle}>
                <ChipLabel kind="you">{youLabel}</ChipLabel>
                <p style={questionStyle}>{question || '…'}</p>
                <ChipLabel kind="assistant">
                  {headerTitle.split(' ').slice(-1)[0] ?? 'AI'}
                </ChipLabel>
                <TypingDots />
              </div>
            ) : null}

            {history.map((ex) => (
              <ExchangeCard
                key={ex.id}
                exchange={ex}
                assistantLabel={headerTitle.split(' ').slice(-1)[0] ?? 'AI'}
                noopExplainer={noopExplainer}
                youLabel={youLabel}
              />
            ))}
          </div>

          {error ? (
            <div
              role="alert"
              style={{
                padding: '10px 14px',
                margin: '0 12px 12px',
                borderRadius: 10,
                background: 'rgba(239,68,68,0.06)',
                border: '1px solid rgba(239,68,68,0.20)',
                color: '#991b1b',
                fontSize: 12,
                lineHeight: 1.5,
              }}
            >
              {error}
              {retryAfterSec !== null && retryAfterSec > 0 ? (
                <span
                  style={{
                    display: 'block',
                    marginTop: 4,
                    color: 'var(--color-on-surface-muted, #64748b)',
                  }}
                >
                  {retryAfterLabel(retryAfterSec)}
                </span>
              ) : null}
            </div>
          ) : null}

          <form
            onSubmit={submit}
            style={{
              padding: 12,
              borderTop: '1px solid var(--color-border)',
              background: 'var(--tint-subtle)',
              display: 'grid',
              gridTemplateColumns: '1fr auto',
              gap: 8,
            }}
          >
            <textarea
              ref={inputRef}
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  void submit(e);
                }
              }}
              placeholder={placeholder}
              rows={2}
              maxLength={1000}
              disabled={pending || retryAfterSec !== null}
              style={{
                resize: 'none',
                padding: '10px 12px',
                fontSize: 14,
                fontFamily: 'inherit',
                background: 'var(--color-surface, #ffffff)',
                border: '1px solid var(--color-border-strong)',
                borderRadius: 12,
                outline: 'none',
                color: 'var(--color-on-surface, #0f172a)',
                lineHeight: 1.4,
              }}
            />
            <button
              type="submit"
              disabled={!question.trim() || pending || retryAfterSec !== null}
              aria-label="Send"
              style={{
                width: 44,
                height: 44,
                alignSelf: 'end',
                borderRadius: 12,
                border: 'none',
                cursor:
                  !question.trim() || pending || retryAfterSec !== null ? 'not-allowed' : 'pointer',
                background:
                  !question.trim() || pending || retryAfterSec !== null
                    ? 'var(--color-border)'
                    : 'linear-gradient(135deg, var(--color-primary, #475569) 0%, var(--color-primary-light, #334155) 100%)',
                color:
                  !question.trim() || pending || retryAfterSec !== null
                    ? '#94a3b8'
                    : 'var(--color-primary-fg, #ffffff)',
                display: 'grid',
                placeItems: 'center',
              }}
            >
              <ArrowUpIcon />
            </button>
          </form>

          {history.length > 0 ? (
            <button
              type="button"
              onClick={clearHistory}
              style={{
                background: 'transparent',
                border: 'none',
                color: 'var(--color-on-surface-muted, #94a3b8)',
                fontSize: 11,
                padding: '6px 16px 10px',
                cursor: 'pointer',
                textAlign: 'left',
                fontFamily: 'inherit',
              }}
            >
              {clearHistoryLabel}
            </button>
          ) : null}
        </div>
      ) : null}
    </>
  );
}

function ExchangeCard({
  exchange,
  assistantLabel,
  noopExplainer,
  youLabel,
}: {
  readonly exchange: Exchange;
  readonly assistantLabel: string;
  readonly noopExplainer: string;
  readonly youLabel: string;
}) {
  const isNoop = exchange.provider === 'noop';
  return (
    <article style={exchangeCardStyle}>
      <ChipLabel kind="you">{youLabel}</ChipLabel>
      <p style={questionStyle}>{exchange.question}</p>
      <ChipLabel kind="assistant">
        {assistantLabel}
        {isNoop ? (
          <span
            style={{
              marginLeft: 6,
              fontSize: 9,
              padding: '2px 6px',
              borderRadius: 999,
              background: 'rgba(245,158,11,0.16)',
              color: '#b45309',
              fontWeight: 600,
              letterSpacing: 0.3,
            }}
          >
            STUB
          </span>
        ) : exchange.cacheHit ? (
          <span
            style={{
              marginLeft: 6,
              fontSize: 9,
              padding: '2px 6px',
              borderRadius: 999,
              background: 'rgba(71,85,105,0.10)',
              color: 'var(--color-primary, #475569)',
              fontWeight: 600,
              letterSpacing: 0.3,
            }}
            title="Answer served from cache — zero token cost"
          >
            CACHED
          </span>
        ) : null}
      </ChipLabel>
      <p
        style={{
          margin: '4px 0 0 0',
          fontSize: 13,
          lineHeight: 1.55,
          whiteSpace: 'pre-wrap',
        }}
      >
        {exchange.answer}
      </p>
      {isNoop ? (
        <p
          style={{
            margin: '8px 0 0 0',
            fontSize: 11,
            color: 'var(--color-on-surface-muted, #64748b)',
            lineHeight: 1.5,
            padding: '8px 10px',
            background: 'rgba(245,158,11,0.06)',
            border: '1px solid rgba(245,158,11,0.20)',
            borderRadius: 8,
          }}
        >
          {noopExplainer}
        </p>
      ) : null}
    </article>
  );
}

function EmptyState({
  body,
  suggestions,
  onApply,
}: {
  readonly body: string;
  readonly suggestions: readonly string[];
  readonly onApply: (text: string) => void;
}) {
  return (
    <div style={{ display: 'grid', gap: 10 }}>
      <p
        style={{
          margin: 0,
          fontSize: 13,
          color: 'var(--color-on-surface-muted, #475569)',
          lineHeight: 1.5,
        }}
      >
        {body}
      </p>
      <div style={{ display: 'grid', gap: 6 }}>
        {suggestions.map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => onApply(s)}
            style={{
              textAlign: 'left',
              background: 'rgba(var(--color-primary-rgb), 0.04)',
              border: '1px solid rgba(var(--color-primary-rgb), 0.18)',
              borderRadius: 10,
              padding: '8px 12px',
              fontSize: 12,
              color: 'var(--color-primary, #475569)',
              cursor: 'pointer',
              fontFamily: 'inherit',
              fontWeight: 500,
            }}
          >
            {s}
          </button>
        ))}
      </div>
    </div>
  );
}

function ChipLabel({
  kind,
  children,
}: {
  readonly kind: 'you' | 'assistant';
  readonly children: React.ReactNode;
}) {
  const palette =
    kind === 'you'
      ? { bg: 'var(--color-border)', fg: 'var(--color-on-surface-muted, #475569)' }
      : { bg: 'rgba(71,85,105,0.10)', fg: 'var(--color-primary, #475569)' };
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        fontSize: 10,
        letterSpacing: 1,
        textTransform: 'uppercase',
        fontWeight: 700,
        padding: '3px 8px',
        borderRadius: 999,
        background: palette.bg,
        color: palette.fg,
        width: 'fit-content',
      }}
    >
      {children}
    </span>
  );
}

function TypingDots() {
  return (
    <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
      {[0, 0.15, 0.3].map((delay) => (
        <span
          key={delay}
          style={{
            width: 7,
            height: 7,
            borderRadius: 999,
            background: 'var(--color-primary, #475569)',
            animation: `kilatAskDot 1.1s ${delay}s infinite ease-in-out`,
          }}
        />
      ))}
    </div>
  );
}

const exchangeCardStyle: React.CSSProperties = {
  padding: 12,
  borderRadius: 12,
  background: 'var(--color-surface, #ffffff)',
  border: '1px solid var(--color-border)',
  display: 'grid',
  gap: 6,
};

const questionStyle: React.CSSProperties = {
  margin: '2px 0 6px 0',
  fontSize: 13,
  color: 'var(--color-on-surface-muted, #475569)',
  lineHeight: 1.5,
  fontStyle: 'italic',
};

function SparkleIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
      <path
        d="M8 1.5l1.4 3.7L13 6.6l-3.6 1.4L8 11.7 6.6 8 3 6.6l3.6-1.4L8 1.5z"
        fill="currentColor"
      />
      <path
        d="M12.5 10.5l.6 1.5 1.5.6-1.5.6-.6 1.5-.6-1.5-1.5-.6 1.5-.6.6-1.5z"
        fill="currentColor"
      />
    </svg>
  );
}

function ArrowUpIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden>
      <path
        d="M9 14V4M9 4L4 9M9 4l5 5"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
